import { Globe, LogOut, User } from 'react-feather';
import { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { Locale } from '__generated__/graphql';

import { Div } from '../../common/UI/Generic';
import { ExportsContext } from '../Exports/ExportsProvider';
import { Permission } from '../../__generated__/graphql';
import { supportedLocales } from '../../i18n';
import { useLogout } from '../../common/Auth/useAuth';
import { useSession } from '../Auth/SessionProvider';
import EditCurrentUserMutation from '../../participant/Common/EditCurrentUserMutation';
import SC from '../../common/UI/SC';
import UI from '../../common/UI';
import config from '../../config';
import useLocale from '../../common/useLocale';
import useMutation from '../../api/useMutation';
import useOnClickOutside from '../../common/useOnClickOutside';
import useProject from '../useProject';

const UserNav = () => {
  const { t } = useTranslation();
  const { user, hasPermission } = useSession();
  const { locale } = useLocale();
  const [logout] = useLogout();
  const project = useProject();

  const { pendingExportsCount, openExportsModal } = useContext(ExportsContext);

  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const projectDropdown = useRef();

  useOnClickOutside(projectDropdown, () => setProjectDropdownOpen(false));

  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const userDropdown = useRef();
  const [showLanguages, setShowLanguages] = useState(false);

  useOnClickOutside(userDropdown, () => {
    setUserDropdownOpen(false);
    setShowLanguages(false);
  });

  const toggleUserDropdown = () => {
    setShowLanguages(userDropdownOpen ? false : showLanguages);
    setUserDropdownOpen((open) => !open);
  };

  const name = user.first_name || user.last_name || user.email;

  const [editLocale] = useMutation(
    EditCurrentUserMutation,
  );

  const handleChangeLocale = (locale: Locale) => {
    editLocale({
      variables: {
        input: {
          locale,
        },
      },
    });
  };

  const hasExportsPermission = project && hasPermission(project.id, [Permission.exports]);
  const hasAdminPermission = project && hasPermission(project.id, [Permission.admin]);

  const showProjectNav = project || user.project_memberships.length > 0;

  return (
    <UserNavContainer>
      {showProjectNav && (
        <UserNavItem
          ref={projectDropdown}
          onClick={() => setProjectDropdownOpen((open) => !open)}
          sc={{ active: projectDropdownOpen }}
        >
          <UI.Dropdown sc={{ open: projectDropdownOpen, alignH: 'right', alignV: 'bottom' }}>
            {project && (hasExportsPermission || hasAdminPermission) && (
              <UI.Div>
                {hasExportsPermission && (
                  <UI.A onClick={() => openExportsModal()} role="button">
                    {t('nav.exports')}
                  </UI.A>
                )}
                {hasAdminPermission && (
                  <UI.NavLink to={`/${project.id}/project`}>
                    {t('nav.settings')}
                  </UI.NavLink>
                )}
              </UI.Div>
            )}
            {(!project || user.project_memberships.length > 1) && (
              <ProjectsContainer>
                <NavHeading>{t('nav.switch_project')}</NavHeading>
                {user.project_memberships.filter((membership) => membership.project.id !== project?.id)
                  .map(({ project: linkedProject }) => (
                    <UI.A
                      href={`${config.backendPath}/${linkedProject.id}`}
                      sc={{ noWrap: true }}
                      key={linkedProject.id}
                    >
                      {linkedProject.organisation_name.substr(0, 30)}
                      {linkedProject.organisation_name.length > 30 && <>&hellip;</>}
                    </UI.A>
                  ))}
              </ProjectsContainer>
            )}
            {project && (
              <UI.Div>
                <UI.NavLink to="/projects/add">
                  {t('nav.new_project')}
                </UI.NavLink>
              </UI.Div>
            )}
          </UI.Dropdown>
          <UI.Span>
            {pendingExportsCount > 0 && (
              <>
                <UI.Icon sc={{ mr: 1.5 }}>
                  <UI.Loader sc={{ brand: 'secondary.300' }} />
                </UI.Icon>
                {t('nav.exporting', { count: pendingExportsCount })}
              </>
            )}
            {pendingExportsCount === 0 && (
              <>
                {project && project.organisation_name.substr(0, 18).trim()}
                {project && project.organisation_name.length > 18 && <>&hellip;</>}
                {!project && t('nav.projects')}
              </>
            )}
            {' '}
            <UI.Caret />
          </UI.Span>
        </UserNavItem>
      )}

      <UserNavItem
        ref={userDropdown}
        onClick={() => toggleUserDropdown()}
        sc={{ active: userDropdownOpen }}
      >
        <UI.Dropdown sc={{ open: userDropdownOpen, alignH: 'right', alignV: 'bottom' }}>
          <UI.Div>
            <NavHeading>
              {user.first_name ? `${user.first_name} ${user.last_name}` : user.email}
            </NavHeading>
            <UI.A href="/me">
              <UI.Icon sc={{ mr: 1 }}>
                <User />
              </UI.Icon>
              {t('nav.my_account')}
            </UI.A>
            <UI.A
              onClick={(event: Event) => {
                event.stopPropagation();
                setShowLanguages((open) => !open);
              }}
            >
              <UI.Icon sc={{ mr: 1 }}>
                <Globe />
              </UI.Icon>
              {t('language')}
              {' '}
              <UI.Caret sc={{ muted: true }} />
            </UI.A>
            <UI.AnimateHeight isVisible={showLanguages}>
              {supportedLocales.current.map((localeOption) => (
                <UI.A
                  onClick={() => handleChangeLocale(localeOption)}
                  sc={{ strong: localeOption === locale }}
                  style={{ paddingLeft: 31 }}
                  key={localeOption}
                >
                  {t(`common:locales.${localeOption}`)}
                </UI.A>
              ))}
            </UI.AnimateHeight>
            <UI.A onClick={() => logout()}>
              <UI.Icon sc={{ mr: 1 }}>
                <LogOut />
              </UI.Icon>
              {t('nav.logout')}
            </UI.A>
          </UI.Div>
        </UI.Dropdown>
        <UI.Span>
          <UI.ProfilePic sc={{ size: 30 }}>
            <UI.Span><UI.Span>{name.charAt(0)}</UI.Span></UI.Span>
          </UI.ProfilePic>
          {' '}
          <UI.Caret />
        </UI.Span>
      </UserNavItem>
    </UserNavContainer>
  );
};

export const UserNavContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    font-size: ${13 / 16}rem;

    @media ${theme.devices.sm} {
      height: 60;
    }

    @media ${theme.devices.md} {
      height: ${theme.navBarHeight.md}px;
      margin-left: auto;
    }
  `}
`;

export const ProjectsContainer = styled.div`
  max-height: 50vh;
  overflow-y: auto;
`;

interface UserNavItemProps {
  active?: boolean;
}

const UserNavItem = styled(Div)<SC<UserNavItemProps>>`
  ${({ sc: { active }, theme }) => css`
    position: relative;
    display: inline-block;

    > ${UI.Span} {
      font-weight: 500;
      cursor: pointer;
      line-height: 36px;
      display: inline-block;
      opacity: 0.8;
      transition: all 0.15s ease-in-out;

      ${UI.ProfilePic} {
        background: ${theme.colors.secondary[300]};
        color: ${theme.colors.white};
        text-shadow: 0 1px 1px rgba(0, 0, 0, .15);
        border: none;
      }
    }

    ${UI.Caret} {
      border-top-color: ${theme.colors.white};
    }

    &:hover > ${UI.Span} {
      opacity: 1;
    }

    @media ${theme.devices.md} {
      ${active && css`
        > ${UI.Span} {
          color: ${theme.colors.white};
        }
      `}
    }

    & + & {
      margin-left: ${theme.gutter}px;
    }

    ${UI.Dropdown} {
      min-width: 150px;
      max-width: 300px;
      color: ${theme.colors.gray[500]};
      background: ${theme.colors.gray[900]};
      border-radius: ${theme.borderRadiuses.md}px;

      > div {
        padding: 5px 0;
      }

      > div + div {
        border-top: 1px solid ${theme.colors.gray[800]};
      }

      ${NavHeading} {
        display: block;
        padding: ${theme.gutter / 4}px ${theme.gutter / 2}px;
      }

      ${UI.NavLink}, ${UI.Link}, ${UI.A} {
        display: block;
        padding: ${theme.gutter / 4}px ${theme.gutter / 2}px;
        line-height: 1.5;
        color ${theme.colors.white};

        &:hover {
          background: ${theme.colors.gray[800]};
        }
      }
    }
  `}
`;

const NavHeading = styled(UI.Span)``;

export default UserNav;
